import { useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, RotateCcw, XCircle } from 'lucide-react';
import { useGetVideoQuizQuery, useSubmitQuizMutation } from '../../../store/api/apiSlice';
import type { SubmitQuizResponse } from '../../../types';
import { readableError } from '../lib';

interface QuizPanelProps {
    videoId: string;
    courseId: string;
    /** Called after any successful submission so the parent can refetch course state. */
    onSubmitted: (result: SubmitQuizResponse) => void;
}

export default function QuizPanel({ videoId, courseId, onSubmitted }: QuizPanelProps) {
    const { data: quiz, isLoading, isError, error, refetch } = useGetVideoQuizQuery(videoId);
    const [submitQuiz, { isLoading: isSubmitting }] = useSubmitQuizMutation();

    const [answers, setAnswers] = useState<Record<string, string>>({});
    const [result, setResult] = useState<SubmitQuizResponse | null>(null);
    const [submitError, setSubmitError] = useState('');

    const resultById = useMemo(() => {
        const map: Record<string, SubmitQuizResponse['results'][number]> = {};
        result?.results.forEach((r) => {
            map[r.questionId] = r;
        });
        return map;
    }, [result]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
        );
    }

    if (isError || !quiz) {
        return (
            <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {readableError(error, 'Could not load the quiz.')}
            </div>
        );
    }

    const totalQuestions = quiz.questions.length;
    const answeredCount = Object.keys(answers).length;

    const handleSubmit = async () => {
        setSubmitError('');
        try {
            const res = await submitQuiz({
                videoId,
                courseId,
                answers: Object.entries(answers).map(([questionId, optionId]) => ({ questionId, optionId })),
            }).unwrap();
            setResult(res);
            onSubmitted(res);
        } catch (err) {
            setSubmitError(readableError(err, 'Failed to submit the quiz.'));
        }
    };

    const handleRetake = () => {
        setResult(null);
        setAnswers({});
        setSubmitError('');
        refetch();
    };

    return (
        <div>
            {/* Result banner */}
            {result && (
                <div
                    className={`mb-6 flex items-start gap-3 rounded-2xl border p-4 ${
                        result.passed ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                    }`}
                >
                    {result.passed ? (
                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
                    ) : (
                        <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                    )}
                    <div>
                        <p className={`font-semibold ${result.passed ? 'text-green-800' : 'text-red-800'}`}>
                            {result.passed ? 'Passed!' : 'Not quite yet'} — scored {result.score}% (need{' '}
                            {result.passingScore}%)
                        </p>
                        <p className="mt-0.5 text-sm text-gray-600">
                            {result.correctCount} of {result.totalQuestions} correct.
                            {result.passed
                                ? ' The next lesson is now unlocked.'
                                : ' Review the answers below and try again.'}
                        </p>
                    </div>
                </div>
            )}

            {/* Questions */}
            <ol className="space-y-6">
                {quiz.questions.map((q, qi) => {
                    const r = resultById[q.id];
                    return (
                        <li key={q.id} className="rounded-2xl border border-gray-100 bg-gray-50 p-5">
                            <p className="mb-3 font-medium text-gray-900">
                                {qi + 1}. {q.text}
                            </p>
                            <div className="space-y-2">
                                {q.options.map((opt) => {
                                    const selected = answers[q.id] === opt.id;
                                    // After grading: green for the correct option, red for a wrong pick.
                                    const isCorrectOption = r && r.correctOptionId === opt.id;
                                    const isWrongPick = r && r.selectedOptionId === opt.id && !r.correct;
                                    return (
                                        <label
                                            key={opt.id}
                                            className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm transition-colors ${
                                                isCorrectOption
                                                    ? 'border-green-300 bg-green-50 text-green-900'
                                                    : isWrongPick
                                                      ? 'border-red-300 bg-red-50 text-red-900'
                                                      : selected
                                                        ? 'border-gray-900 bg-white text-gray-900'
                                                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                                            } ${result ? 'cursor-default' : ''}`}
                                        >
                                            <input
                                                type="radio"
                                                name={q.id}
                                                value={opt.id}
                                                checked={selected}
                                                disabled={!!result}
                                                onChange={() => setAnswers((a) => ({ ...a, [q.id]: opt.id }))}
                                                className="h-4 w-4 accent-[#FF6B35]"
                                            />
                                            <span className="flex-1">{opt.text}</span>
                                            {isCorrectOption && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                                            {isWrongPick && <XCircle className="h-4 w-4 text-red-600" />}
                                        </label>
                                    );
                                })}
                            </div>
                        </li>
                    );
                })}
            </ol>

            {submitError && (
                <div className="mt-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    {submitError}
                </div>
            )}

            {/* Actions */}
            <div className="mt-6 flex items-center justify-between gap-4">
                {!result ? (
                    <>
                        <span className="text-xs text-gray-500">
                            {answeredCount}/{totalQuestions} answered
                            {answeredCount < totalQuestions ? ' — unanswered count as wrong' : ''}
                        </span>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={isSubmitting || answeredCount === 0}
                            className="inline-flex items-center gap-2 rounded-full bg-[#FF6B35] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[#ff5722] disabled:opacity-50"
                        >
                            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                            Submit quiz
                        </button>
                    </>
                ) : !result.passed ? (
                    <button
                        type="button"
                        onClick={handleRetake}
                        className="ml-auto inline-flex items-center gap-2 rounded-full bg-gray-900 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-gray-800"
                    >
                        <RotateCcw className="h-4 w-4" /> Retake quiz
                    </button>
                ) : null}
            </div>
        </div>
    );
}
